import { SCOPE_CATEGORY } from "@/lib/constants";

export const emissionSlotsPerScope = (frequency) => {
  const f = String(frequency || "yearly");
  return f === "monthly" ? 12 : f === "quarterly" ? 4 : 1;
};

const NXMAP_REQUIRED_KEYS = [
  "organizationBoundary",
  "ghgReportPurpose",
  "threshold",
  "reportingStandard",
  "baseYear",
  "baseYearStart",
  "baseYearEnd",
  "reportingYear",
  "reportingPeriodStart",
  "reportingPeriodEnd",
];

const NXMAP_CRITERION_FIELDS = [
  "dataCredibility",
  "emissionFactorSource",
  "technologyFeasibility",
  "occurrenceFrequency",
  "emissionVolume",
];

const hasNxmapFields = (data = {}) =>
  NXMAP_REQUIRED_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(data, key),
  );

const isFilledValue = (value) => {
  if (value === null || typeof value === "undefined") return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const normalizeMaterialityDetails = (rowDetails, materialityAssessment) => {
  if (rowDetails && typeof rowDetails === "object") return rowDetails;
  if (Array.isArray(materialityAssessment)) {
    return materialityAssessment.reduce((acc, item) => {
      if (item?.categoryKey) acc[item.categoryKey] = item;
      return acc;
    }, {});
  }
  return {};
};

export const calcNxplanProgress = ({
  form = {},
  freq = {},
  emissions = {},
  baseline = {},
}) => {
  const requiredKeys = (() => {
    const keys = [
      "organizationBoundary",
      "baseYear",
      "targetYear",
      "reportingYear",
      "isSBTi",
    ];
    if (form.isSBTi === "yes") {
      keys.push("sbtiTemperature");
      keys.push("targetType");
      keys.push("regressionModel");
    } else if (form.isSBTi === "no") {
      keys.push("targetReduction");
    }
    return keys;
  })();

  const baseFilled = requiredKeys.reduce((acc, key) => {
    const value = form[key];
    if (key === "targetReduction") {
      const n = Number(value);
      return acc + (!Number.isNaN(n) && value !== "" ? 1 : 0);
    }
    return acc + (String(value ?? "").trim().length > 0 ? 1 : 0);
  }, 0);

  const scopeKeys = ["scope1", "scope2", "scope3"];
  const emissionsRequired = scopeKeys.reduce(
    (sum, key) => sum + emissionSlotsPerScope(freq[key] || "yearly"),
    0,
  );
  const emissionsFilled = scopeKeys.reduce((sum, key) => {
    const size = emissionSlotsPerScope(freq[key] || "yearly");
    const arrRaw = emissions[key] || [];
    const arr = Array.isArray(arrRaw) ? arrRaw.slice(0, size) : [];
    const filled = arr.filter((item) => {
      const val =
        typeof item === "object" && item !== null ? item.emission : item;
      const n = Number(val);
      return String(val ?? "").trim() !== "" && !Number.isNaN(n);
    }).length;
    return sum + filled;
  }, 0);

  const baselineRequired = scopeKeys.length;
  const baselineFilled = scopeKeys.reduce((sum, key) => {
    const raw = baseline?.[key];
    const str = String(raw ?? "").trim();
    if (!str) return sum;
    const n = Number(str);
    return sum + (!Number.isNaN(n) && n >= 0 ? 1 : 0);
  }, 0);

  const total =
    (requiredKeys.length || 1) + emissionsRequired + baselineRequired;
  const filled = baseFilled + emissionsFilled + baselineFilled;
  return Math.round((filled / Math.max(1, total)) * 100);
};

export const calcNxmapProgress = ({
  form = {},
  rowDetails,
  materialityAssessment,
} = {}) => {
  const baseFilled = NXMAP_REQUIRED_KEYS.reduce((acc, key) => {
    const value = form[key];
    if (key === "threshold") {
      const n = Number(value);
      return acc + (!Number.isNaN(n) && value !== "" ? 1 : 0);
    }
    return acc + (isFilledValue(value) ? 1 : 0);
  }, 0);

  const details = normalizeMaterialityDetails(
    rowDetails,
    materialityAssessment,
  );
  const categories = Array.isArray(SCOPE_CATEGORY) ? SCOPE_CATEGORY : [];

  const detailTotals = categories.reduce(
    (acc, category) => {
      const detail = details?.[category.key] || {};
      const reqByLaw = detail.reqByLaw;
      const reqByLawFilled = isFilledValue(reqByLaw);
      const disclosureYearFilled = isFilledValue(detail.targetYear);

      acc.required += 2;
      acc.filled += (reqByLawFilled ? 1 : 0) + (disclosureYearFilled ? 1 : 0);

      const isNonReqByLaw = reqByLaw === false || reqByLaw === "false";
      if (isNonReqByLaw) {
        acc.required += NXMAP_CRITERION_FIELDS.length;
        NXMAP_CRITERION_FIELDS.forEach((field) => {
          if (isFilledValue(detail[field])) acc.filled += 1;
        });
      }
      return acc;
    },
    { required: 0, filled: 0 },
  );

  const total = NXMAP_REQUIRED_KEYS.length + detailTotals.required;
  const filled = baseFilled + detailTotals.filled;
  return Math.round((filled / Math.max(1, total)) * 100);
};

export const calculateNxmapProgress = (data = {}) => {
  if (!data || typeof data !== "object") return 0;

  const form = data.form || data;
  // if (
  //   form?.ghgReportPurpose === "compliance" ||
  //   form?.ghgReportPurpose === "stakeholders_requirement"
  // ) {
  //   NXMAP_REQUIRED_KEYS.push("assurance");
  // }
  const hasFormFields = hasNxmapFields(form);
  const hasDetails =
    data.rowDetails ||
    (Array.isArray(data.materialityAssessment) &&
      data.materialityAssessment.length > 0);

  if (hasFormFields || hasDetails) {
    return calcNxmapProgress({
      form,
      rowDetails: data.rowDetails,
      materialityAssessment: data.materialityAssessment,
    });
  }

  const { completedSteps = {}, totalSteps = 4 } = data;
  const total = Math.max(1, Number(totalSteps) || 4);
  const done = Object.values(completedSteps || {}).filter(Boolean).length;
  return Math.round((done / total) * 100);
};
